/**
 * ClientNeedsEditor — the "Needs / Prescription" section on the Client 360 profile.
 *
 * Renders the client's drill-library taxonomy needs as colored pills and lets the
 * practitioner add/remove them via a taxonomy picker (restricted to NEEDS_CATEGORIES).
 * Seeding is on-demand: Harry surfaces *suggested* needs from the latest assessment
 * with one-tap accept — nothing is written automatically.
 *
 * @see docs/features/260615-client-management/PLAN.md → Phase 2B P1 (Client → Library)
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Plus, X, Sparkles, Library, Check, Wand2, RotateCcw } from 'lucide-react';
import type { Assessment, Client, ClientNeedsTag, PostureAnalysis } from '@/lib/types';
import {
  NEEDS_CATEGORIES,
  needsTagKey,
  dedupeNeedsTags,
  seedNeedsFromClassification,
} from '@/lib/clients/needs-tags';
import { updateClient } from '@/components/clients/services/clients-api';
import { clientDisplayLabel } from '@/lib/clients/roster-fields';
import { loadColors, getTagColor, colorIsLight, getTagTooltip } from '@/utils/drill-ui';
import { getTaxonomyCategories } from '@/lib/taxonomy-loader';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import logger from '@/lib/logging';

interface ClientNeedsEditorProps {
  client: Client;
  needsTags: ClientNeedsTag[];
  postureAnalyses?: PostureAnalysis[];
  assessments?: Assessment[];
  onNeedsChange: (next: ClientNeedsTag[]) => void;
  onOpenLibrary?: () => void;
  onOpenPlanner?: () => void;
}

function pillStyle(category: string, value: string, ready: boolean): React.CSSProperties {
  if (!ready) return {};
  const color = getTagColor(category, value);
  return { backgroundColor: color, color: colorIsLight(color) ? '#0f172a' : '#ffffff' };
}

export function ClientNeedsEditor({
  client,
  needsTags,
  postureAnalyses,
  assessments,
  onNeedsChange,
  onOpenLibrary,
  onOpenPlanner,
}: ClientNeedsEditorProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const [taxonomy, setTaxonomy] = useState<Record<string, string[]>>({});
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string>(NEEDS_CATEGORIES[0]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        await loadColors();
        const categories = await getTaxonomyCategories();
        if (!active) return;
        const filtered: Record<string, string[]> = {};
        for (const category of NEEDS_CATEGORIES) {
          const options = categories[category]?.options;
          if (Array.isArray(options) && options.length > 0) {
            filtered[category] = options;
          }
        }
        setTaxonomy(filtered);
      } catch (error) {
        logger.error('Failed to load taxonomy for needs editor', error);
      } finally {
        if (active) setReady(true);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const assignedKeys = useMemo(() => new Set(needsTags.map(needsTagKey)), [needsTags]);

  const seeded = useMemo(
    () => seedNeedsFromClassification({ ...client, postureAnalyses, assessments }),
    [client, postureAnalyses, assessments]
  );

  const suggestions = useMemo(
    () => seeded.filter((tag) => !assignedKeys.has(needsTagKey(tag))),
    [seeded, assignedKeys]
  );

  async function persist(next: ClientNeedsTag[]) {
    const deduped = dedupeNeedsTags(next);
    setSaving(true);
    try {
      await updateClient(client.id, {
        needsTags: deduped,
        needsTagsUpdatedAt: new Date().toISOString(),
        needsTagsUpdatedBy: user?.email || user?.uid || 'unknown',
      });
      onNeedsChange(deduped);
    } catch (error) {
      logger.error('Failed to save client needs', error);
      toast({
        title: 'Could not save needs',
        description: 'Your changes were not saved. Check your connection or permissions and try again.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  }

  const addNeed = (tag: ClientNeedsTag) => persist([...needsTags, tag]);
  const removeNeed = (tag: ClientNeedsTag) =>
    persist(needsTags.filter((t) => needsTagKey(t) !== needsTagKey(tag)));
  const acceptAllSuggestions = () => persist([...needsTags, ...suggestions]);

  // Auto-populate the needs from classification on first open (one-time per client).
  // `needsTagsUpdatedAt` is the "initialized" marker: once the practitioner has
  // curated — or intentionally cleared — the needs, we never auto-re-seed. "Reset"
  // restores the classification-derived set on demand.
  const autoInitRef = useRef(false);
  useEffect(() => {
    if (autoInitRef.current) return;
    if (client.needsTagsUpdatedAt) return;
    if (needsTags.length > 0) return;
    if (seeded.length === 0) return;
    autoInitRef.current = true;
    void persist(seeded);
    // persist is intentionally omitted; the ref guarantees a single run per mount.
  }, [client.id, client.needsTagsUpdatedAt, needsTags.length, seeded]);

  async function handleReset() {
    await persist(seeded);
    setResetOpen(false);
  }

  const hasCuratedNeeds = needsTags.length > 0;

  // Shared taxonomy picker body — rendered inside a popover on desktop and a
  // thumb-reachable bottom sheet on mobile.
  const pickerBody = (
    <>
      <div className="flex flex-wrap gap-1 border-b p-2">
        {NEEDS_CATEGORIES.filter((c) => taxonomy[c]?.length).map((category) => (
          <Button
            key={category}
            type="button"
            variant={activeCategory === category ? 'default' : 'ghost'}
            size="sm"
            className="h-8 text-xs"
            onClick={() => setActiveCategory(category)}
          >
            {category}
          </Button>
        ))}
      </div>
      <div className="max-h-[55vh] overflow-y-auto p-2 sm:max-h-64">
        <div className="flex flex-wrap gap-1.5">
          {(taxonomy[activeCategory] ?? [])
            .filter((value) => !assignedKeys.has(`${activeCategory}::${value}`))
            .map((value) => (
              <button
                key={value}
                type="button"
                title={getTagTooltip(value)}
                onClick={() => addNeed({ category: activeCategory, value })}
                className="rounded-full px-3 py-1.5 text-xs font-medium transition hover:opacity-80"
                style={pillStyle(activeCategory, value, ready)}
              >
                {value}
              </button>
            ))}
          {(taxonomy[activeCategory] ?? []).filter(
            (value) => !assignedKeys.has(`${activeCategory}::${value}`)
          ).length === 0 && (
            <p className="px-1 py-2 text-xs text-muted-foreground">
              All {activeCategory.toLowerCase()} tags assigned.
            </p>
          )}
        </div>
      </div>
    </>
  );

  return (
    <Card className="border-none shadow-md" data-testid="client-needs-editor">
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="text-base">Needs / Prescription</CardTitle>
          <CardDescription>
            Drill-library tags for this client — drives Library &amp; Planner filtering
          </CardDescription>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setResetOpen(true)}
            disabled={saving || needsTags.length === 0}
            className="gap-1.5"
            data-testid="client-needs-reset"
            title="Reset needs to the latest assessment"
          >
            <RotateCcw className="h-4 w-4" />
            <span className="hidden sm:inline">Reset</span>
          </Button>
          {onOpenPlanner && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onOpenPlanner}
              className="gap-1.5"
              data-testid="client-needs-open-planner"
            >
              <Wand2 className="h-4 w-4" />
              <span className="hidden sm:inline">Plan drills</span>
            </Button>
          )}
          {onOpenLibrary && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onOpenLibrary}
              className="gap-1.5"
              data-testid="client-needs-open-library"
            >
              <Library className="h-4 w-4" />
              <span className="hidden sm:inline">Open Drill Library</span>
            </Button>
          )}
          {/* Desktop: anchored popover. */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                size="sm"
                disabled={saving || !ready}
                className="hidden gap-1.5 md:inline-flex"
                data-testid="client-needs-add"
              >
                <Plus className="h-4 w-4" />
                Add need
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="end">
              {pickerBody}
            </PopoverContent>
          </Popover>

          {/* Mobile: thumb-reachable bottom sheet. */}
          <Sheet>
            <SheetTrigger asChild>
              <Button
                type="button"
                disabled={saving || !ready}
                className="h-11 gap-1.5 rounded-lg md:hidden"
                data-testid="client-needs-add-mobile"
              >
                <Plus className="h-4 w-4" />
                Add need
              </Button>
            </SheetTrigger>
            <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto p-0">
              <SheetHeader className="p-3 pb-1 text-left">
                <SheetTitle className="text-sm">Add a need</SheetTitle>
              </SheetHeader>
              {pickerBody}
            </SheetContent>
          </Sheet>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {needsTags.length === 0 ? (
          <p className="text-sm text-muted-foreground italic" data-testid="client-needs-empty">
            No needs assigned yet. Add taxonomy tags or accept a suggestion below.
          </p>
        ) : (
          <div className="flex flex-wrap gap-1.5" data-testid="client-needs-pills">
            {needsTags.map((tag) => (
              <span
                key={needsTagKey(tag)}
                className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium"
                style={pillStyle(tag.category, tag.value, ready)}
                title={getTagTooltip(tag.value)}
              >
                {tag.value}
                <button
                  type="button"
                  aria-label={`Remove ${tag.value}`}
                  disabled={saving}
                  onClick={() => removeNeed(tag)}
                  className="-mr-0.5 rounded-full p-0.5 transition hover:bg-black/15"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        {suggestions.length > 0 && (
          <div className="rounded-lg border border-dashed bg-muted/30 p-3" data-testid="client-needs-suggestions">
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                <Sparkles className="h-3.5 w-3.5" />
                {hasCuratedNeeds
                  ? 'Classification may have changed — review suggested needs'
                  : 'Suggested from the latest assessment'}
              </p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 gap-1 text-xs"
                disabled={saving}
                onClick={acceptAllSuggestions}
                data-testid="client-needs-accept-all"
              >
                <Check className="h-3.5 w-3.5" />
                Accept all
              </Button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {suggestions.map((tag) => (
                <button
                  key={needsTagKey(tag)}
                  type="button"
                  disabled={saving}
                  title={getTagTooltip(tag.value)}
                  onClick={() => addNeed(tag)}
                  className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ring-black/10 transition hover:opacity-80"
                  style={pillStyle(tag.category, tag.value, ready)}
                >
                  <Plus className="h-3 w-3" />
                  {tag.value}
                </button>
              ))}
            </div>
          </div>
        )}
      </CardContent>

      <AlertDialog open={resetOpen} onOpenChange={(open) => !saving && setResetOpen(open)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset needs to the latest assessment?</AlertDialogTitle>
            <AlertDialogDescription>
              This clears any manual curation and restores the needs suggested by{' '}
              {clientDisplayLabel(client)}&apos;s latest assessment
              {seeded.length === 0 ? ' (no suggestions available — needs will be cleared).' : '.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={saving}
              onClick={(e) => {
                e.preventDefault();
                void handleReset();
              }}
            >
              {saving ? 'Resetting…' : 'Reset needs'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
